import React, { ChangeEvent, useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { Input } from "../ui/input";
import { useGetSparksQuery } from "../../../redux/reducers/apiSlice";
import { Spark } from "../../../redux/reducers/types";
import { customConsole } from "../../utils/helper";
import { useTierLimits, FREE_TIER_LIMITS } from "../../hooks/useTierLimits";
import toast from "react-hot-toast";

const StepSpark = ({ formik, sparksLength = 3 }) => {
  const tierInfo = useTierLimits();
  // Use tier limit for free users, or provided sparksLength for paid users
  const maxSparks = tierInfo.isPaid ? sparksLength : Math.min(sparksLength, FREE_TIER_LIMITS.MAX_SPARKS);
  const { data: sparksData, isLoading: sparksDataLoading, error: sparksError } =
    useGetSparksQuery();
  const [searchInput, setSearchInput] = useState("");
  const [sparks, setSparks] = useState<Spark[]>([]);

  const filterSparks = (search: string, sparksData) => {
    return sparksData?.filter((spark) => {
      return spark.name.toLowerCase().startsWith(search.toLowerCase());
    });
  };

  const isCheckDisabled = (value) => {
    const currentCount = formik.values.sparks?.length || 0;
    if (
      currentCount >= maxSparks &&
      !formik.values.sparks.includes(value)
    ) {
      return true;
    }
    return false;
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);
  };

  useEffect(() => {
    if (searchInput !== "") {
      const filteredSparks = filterSparks(searchInput, sparksData);
      setSparks(filteredSparks);
    } else {
      if (sparksData) {
        setSparks(sparksData);
      } else {
        setSparks([]);
      }
    }
  }, [searchInput, sparksData]);

  const handleSparkClick = (sparkName: string) => {
    const currentSparks = [...(formik.values.sparks || [])];
    if (currentSparks.includes(sparkName)) {
      formik.setFieldValue(
        "sparks",
        currentSparks.filter((spark) => spark !== sparkName)
      );
    } else if (currentSparks.length < maxSparks) {
      formik.setFieldValue("sparks", [...currentSparks, sparkName]);
    } else {
      // Limit reached
      if (!tierInfo.isPaid) {
        toast.error(
          `Free tier limit: Maximum ${FREE_TIER_LIMITS.MAX_SPARKS} Sparks allowed. Upgrade to paid tier for unlimited Sparks.`,
          { duration: 5000 }
        );
      } else {
        toast.error(`Maximum ${maxSparks} Sparks allowed.`);
      }
    }
  };

  // Debug logging
  useEffect(() => {
    if (sparksData) {
      customConsole("log", "Sparks data loaded:", sparksData.length, "sparks");
    }
    if (sparksDataLoading) {
      customConsole("log", "Loading sparks...");
    }
    if (sparksError) {
      customConsole("log", "Sparks error:", sparksError);
    }
  }, [sparksData, sparksDataLoading, sparksError]);

  if (sparksDataLoading)
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner color="red" />
      </div>
    );
  
  if (sparksError) {
    return (
      <div className="flex flex-col justify-center items-center py-12 space-y-4">
        <p className="text-red-600">Error loading sparks</p>
        <p className="text-sm text-gray-500">Please check your connection and try again.</p>
        <p className="text-xs text-gray-400">Error: {sparksError?.data?.message || sparksError?.status || 'Unknown error'}</p>
      </div>
    );
  }
  
  if (!sparksData || sparksData.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center py-12 space-y-4">
        <p className="text-gray-600">No sparks available.</p>
        <p className="text-sm text-gray-500">The backend may not have sparks data loaded. Check backend logs.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search Input */}
      <div>
        <Input
          placeholder="🔍 Search sparks..."
          value={searchInput}
          onChange={handleChange}
          className="w-full"
        />
      </div>

      {/* Selected Count */}
      <div className="text-center space-y-1">
        <span className="text-sm text-gray-600">
          Selected: {formik.values.sparks?.length || 0}/{maxSparks}
        </span>
        {!tierInfo.isPaid && (
          <div className="text-xs text-amber-600">
            Free tier: {FREE_TIER_LIMITS.MAX_SPARKS} Sparks max. 
            {formik.values.sparks?.length >= FREE_TIER_LIMITS.MAX_SPARKS && (
              <span className="font-semibold"> Upgrade to paid ($19/month) for unlimited.</span>
            )}
          </div>
        )}
      </div>

      {/* Sparks Grid */}
      {!sparksDataLoading && (
        <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
          {sparks?.map((item) => {
            const isSelected = formik.values.sparks?.includes(item.name);
            const isDisabled = isCheckDisabled(item.name);

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSparkClick(item.name)}
                disabled={isDisabled}
                className={`p-4 border-2 rounded-lg text-left transition ${
                  isSelected
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-gray-300"
                } ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                data-cy={`cypress-spark-${item.id}`}
              >
                <div className="flex items-center gap-3">
                  {item.logo ? (
                    <img
                      src={item.logo}
                      alt={item.name}
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <span className="text-2xl">✨</span>
                  )}
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StepSpark;
